/**
 * ConversationListPane.tsx — left column of the chatter workspace (Design Spec §03/§07).
 *
 * Queue for the active creator: a search box + the creator's conversations
 * (merged across that creator's accounts, sorted most-recent first). Selecting
 * a row does NOT navigate — it lifts the conversation id to the workspace so
 * the centre + right columns update in place (Design Spec §02 "never lose context").
 *
 * Row anatomy (Design Spec §10):
 *   - monogram avatar (accent-filled when active)
 *   - fan display name (nickname › username › "Fan <id>")  +  relative time
 *     (name + preview render BOLD when the last message is inbound + unanswered)
 *   - last-message preview with a "You:" marker for outbound tails
 *     (rows with no traffic read "No messages yet" in muted italic)
 *   - a bold "Reply" badge when the fan's last message is unanswered
 *     (lastMessageDirection === 'inbound') + health badge + unread + per-fan spend
 *
 * The account id is intentionally NOT shown on rows — a chatter works one
 * creator tab at a time, so the per-row account sub-label was noise (owner
 * request 2026-06-27).
 *
 * OB-1 NOTE: untranslated preview rows may still show raw DE — EXPECTED until
 * OB-1 fills translated_text. Do NOT add a translation call here.
 */

import * as React from 'react';
import type { InboxConversationDto } from '@crm/types';
import { AccountHealthBadge, isAccountDead } from './AccountHealthBadge.js';
import { FanAvatar } from './FanAvatar.js';
import { Badge, Button } from '@crm/ui';
import { relativeTime } from '../lib/time.js';
import { formatEurMinor, fanDisplayName } from '../lib/format.js';
import type { HealthMap } from '../lib/health.js';
import type { InboxFilterMode } from '../lib/api-client.js';

/** Filter chips (owner 2026-07-11). 'unread'/'unanswered' are SERVER-side — they search
 *  the whole account, unlike the client-side search/muted toggles below. */
const FILTER_CHIPS: Array<{ mode: InboxFilterMode; label: string }> = [
  { mode: 'all', label: 'All' },
  { mode: 'unread', label: 'Unread' },
  { mode: 'unanswered', label: 'Unanswered' },
];

interface ConversationListPaneProps {
  creatorName: string;
  conversations: InboxConversationDto[];
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  healthMap: HealthMap;
  activeConversationId: string | null;
  onSelect: (conv: InboxConversationDto) => void;
  /** Phase 10: mark a conversation unread (CRM-local; no Maloum call). Optional so the
   *  pane still renders without the affordance. */
  onMarkUnread?: (conv: InboxConversationDto) => void;
  /** Explicit CRM-local mark-READ — clears the unread badge from the list WITHOUT opening the
   *  thread. The counterpart to onMarkUnread: together they toggle the badge from the row.
   *  Required for admins, whose passive auto-mark-read on open is suppressed (T-12-11), so
   *  without this they could add a badge but never remove one. Optional → no affordance. */
  onMarkRead?: (conv: InboxConversationDto) => void;
  /** True while the inbox is being pulled live from Maloum (refresh on open/switch). */
  refreshing?: boolean;
  /** Active filter chip. Optional (with onFilterModeChange) so the pane renders without chips. */
  filterMode?: InboxFilterMode;
  /** Chip click handler — lifts the mode to the workspace (it lives in the query key). */
  onFilterModeChange?: (mode: InboxFilterMode) => void;
  /**
   * Admin all-accounts mode (Phase 12-03, D-04): show each row's creator/account label
   * (`conv.creatorName`) — the "All" tab merges conversations across every creator, so the
   * per-tab creator context that normally makes the account label unnecessary (owner
   * request 2026-06-27) is gone. Omitted/false for the normal per-creator tab (no change).
   */
  showCreatorLabel?: boolean;
  /**
   * Admin "All" tab cursor pagination (Phase 12 follow-up): true when the server reports
   * more pages beyond what's loaded. Renders a "Load more" footer under the rows. Omitted
   * for the per-creator tab (chatter is byte-identical — no footer).
   */
  hasMore?: boolean;
  /** True while the next page is being fetched — disables the footer button + shows a spinner. */
  isLoadingMore?: boolean;
  /** Pull the next server page. Absent → no "Load more" affordance renders. */
  onLoadMore?: () => void;
}

export function ConversationListPane({
  creatorName,
  conversations,
  isLoading,
  isError,
  errorMessage,
  healthMap,
  activeConversationId,
  onSelect,
  onMarkUnread,
  onMarkRead,
  refreshing = false,
  filterMode = 'all',
  onFilterModeChange,
  showCreatorLabel = false,
  hasMore = false,
  isLoadingMore = false,
  onLoadMore,
}: ConversationListPaneProps) {
  const [search, setSearch] = React.useState('');
  // Muted fans are hidden from the active queue by default (Phase 6). The "Muted (N)"
  // toggle reveals them; mute itself is a CRM-local flag (no Maloum call).
  const [showMuted, setShowMuted] = React.useState(false);

  const mutedCount = React.useMemo(
    () => conversations.filter((c) => c.isMuted).length,
    [conversations],
  );

  // Client-side filter — drop muted (unless revealed) then match the search query
  // against fan name (nickname/username) + short id (owner's choice).
  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return conversations.filter((c) => {
      if (c.isMuted && !showMuted) return false;
      if (!q) return true;
      const hay = [
        c.fanNickname ?? '',
        c.fanUsername ?? '',
        c.fanId ?? '',
      ]
        .join(' ')
        .toLowerCase();
      return hay.includes(q);
    });
  }, [conversations, search, showMuted]);

  return (
    <div className="flex flex-col h-full bg-bg-card">
      {/* Search */}
      <div className="p-3 shrink-0 border-b border-border">
        <div className="relative">
          <svg
            className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-text-subtle pointer-events-none"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="11" cy="11" r="7" />
            <path strokeLinecap="round" d="M21 21l-4.3-4.3" />
          </svg>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search fans…"
            aria-label="Search fans"
            className="w-full bg-bg-input text-text border border-border rounded-md pl-8 pr-3 py-2 text-sm placeholder:text-text-subtle focus:outline-none focus:ring-2 focus:ring-accent/60"
          />
        </div>

        {/* Filter chips (owner 2026-07-11) — server-side, account-wide (unlike search). */}
        {onFilterModeChange && (
          <div
            className="mt-2 flex items-center gap-1.5"
            role="group"
            aria-label="Filter conversations"
          >
            {FILTER_CHIPS.map(({ mode, label }) => (
              <button
                key={mode}
                type="button"
                onClick={() => onFilterModeChange(mode)}
                aria-pressed={filterMode === mode}
                className={[
                  'text-xs font-semibold rounded-full px-2.5 py-1 border transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
                  filterMode === mode
                    ? 'border-accent text-accent bg-accent/10'
                    : 'border-border text-text-subtle hover:text-text',
                ].join(' ')}
              >
                {label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* List header */}
      <div className="px-3 py-2 flex items-center justify-between gap-2 shrink-0">
        <span className="text-xs font-semibold uppercase tracking-wide text-text-subtle font-mono truncate">
          {creatorName} · Conversations
        </span>
        <div className="flex items-center gap-2 shrink-0">
          {refreshing && (
            <span
              className="inline-flex items-center gap-1 text-[11px] text-accent font-mono"
              aria-live="polite"
            >
              <span
                className="inline-block w-3 h-3 rounded-full border-2 border-accent/30 border-t-accent animate-spin"
                aria-hidden="true"
              />
              Syncing…
            </span>
          )}
          {mutedCount > 0 && (
            <button
              type="button"
              onClick={() => setShowMuted((v) => !v)}
              aria-pressed={showMuted}
              className={[
                'text-[11px] rounded px-1.5 py-0.5 border transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
                showMuted
                  ? 'border-accent text-accent bg-accent/10'
                  : 'border-border text-text-subtle hover:text-text',
              ].join(' ')}
            >
              {showMuted ? 'Hide muted' : `Muted (${mutedCount})`}
            </button>
          )}
          <span className="text-xs text-text-subtle">
            {filtered.length} fan{filtered.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>

      {/* Rows */}
      <div className="flex-1 overflow-y-auto">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <span className="text-text-muted text-sm">Loading inbox…</span>
          </div>
        ) : isError ? (
          <div className="flex items-center justify-center py-16 px-4 text-center">
            <span className="text-danger text-sm">
              Failed to load inbox: {errorMessage ?? 'Unknown error'}
            </span>
          </div>
        ) : conversations.length === 0 && filterMode !== 'all' ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 px-4 text-center">
            <span className="text-text-muted text-sm">
              {filterMode === 'unread'
                ? 'No unread conversations.'
                : 'No unanswered conversations — all caught up.'}
            </span>
            {onFilterModeChange && (
              <button
                type="button"
                onClick={() => onFilterModeChange('all')}
                className="text-xs text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded"
              >
                Show all
              </button>
            )}
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex items-center justify-center py-16 px-4 text-center">
            <span className="text-text-muted text-sm">
              No conversations for {creatorName} yet.
            </span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 px-4 text-center">
            {search.trim() ? (
              <span className="text-text-muted text-sm">No fans match “{search}”.</span>
            ) : (
              <>
                <span className="text-text-muted text-sm">
                  {filterMode !== 'all'
                    ? `All ${mutedCount} ${filterMode} conversation${mutedCount === 1 ? '' : 's'} ${mutedCount === 1 ? 'is' : 'are'} muted.`
                    : `All ${mutedCount} conversation${mutedCount === 1 ? '' : 's'} for ${creatorName} are muted.`}
                </span>
                {!showMuted && (
                  <button
                    type="button"
                    onClick={() => setShowMuted(true)}
                    className="text-xs text-accent hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60 rounded"
                  >
                    Show muted
                  </button>
                )}
              </>
            )}
          </div>
        ) : (
          <div className="flex flex-col">
            {filtered.map((conv) => (
              <ConversationRow
                key={conv.id}
                conv={conv}
                active={conv.id === activeConversationId}
                healthState={healthMap[conv.accountId] ?? null}
                onSelect={() => onSelect(conv)}
                onMarkUnread={onMarkUnread ? () => onMarkUnread(conv) : undefined}
                onMarkRead={onMarkRead ? () => onMarkRead(conv) : undefined}
                showCreatorLabel={showCreatorLabel}
              />
            ))}
          </div>
        )}

        {/* Admin "All" tab cursor pagination (Phase 12 follow-up) — pull the next server
            page. Rendered outside the client-side search/muted filter branches on purpose:
            those filters only see the loaded page, so an admin hunting for a fan not yet
            loaded can page further from the server. Absent when onLoadMore is not wired
            (per-creator tab / chatter — byte-identical). */}
        {onLoadMore && hasMore && !isLoading && !isError && (
          <div className="flex justify-center py-3">
            <Button variant="ghost" size="sm" onClick={onLoadMore} loading={isLoadingMore}>
              Load more
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------

interface ConversationRowProps {
  conv: InboxConversationDto;
  active: boolean;
  healthState: string | null;
  onSelect: () => void;
  /** Phase 10: mark this conversation unread (CRM-local). Absent → no affordance. */
  onMarkUnread?: () => void;
  /** Explicit mark-READ — clears the unread badge from the row (CRM-local). Absent → no affordance. */
  onMarkRead?: () => void;
  /** Admin all-accounts mode (Phase 12-03, D-04): show conv.creatorName under the row. */
  showCreatorLabel?: boolean;
}

function ConversationRow({
  conv,
  active,
  healthState,
  onSelect,
  onMarkUnread,
  onMarkRead,
  showCreatorLabel,
}: ConversationRowProps) {
  const dead = isAccountDead(healthState);
  const name = fanDisplayName({
    nickname: conv.fanNickname,
    username: conv.fanUsername,
    fanId: conv.fanId,
  });
  // The fan's message is the latest in the thread and we haven't replied →
  // chatter must respond. Drives the bold row treatment + "Reply" badge. Muted
  // conversations are deprioritised, so they never raise the needs-reply signal.
  // Phase 10 (D-10-03/SC1): a CRM-local mark-unread override also raises this signal.
  const needsReply =
    !conv.isMuted &&
    (conv.isMarkedUnread ||
      (!!conv.lastMessagePreview && conv.lastMessageDirection === 'inbound'));
  // Does the row currently display an unread badge? — real Maloum unread OR the CRM-local
  // mark-unread override (D-10-03/SC3). Drives which side of the hover toggle to show.
  const hasUnreadBadge = conv.unread > 0 || conv.isMarkedUnread;
  // Hover-revealed toggle that adds/removes the unread badge (CRM-local; no Maloum call).
  // When a badge is present, offer "Mark read" (removes it); otherwise "Mark unread" (adds it).
  // Admins depend on the "Mark read" side because their passive auto-mark-read on open is
  // suppressed (T-12-11) — the list toggle is their only way to CLEAR a badge without a Maloum
  // action; chatters gain a click alternative to opening. The two sides are mutually exclusive
  // (hasUnreadBadge partitions them). Dead accounts disable all row actions, and each side
  // renders only when its handler is wired.
  const showMarkRead = hasUnreadBadge && !!onMarkRead && !dead;
  const showMarkUnread = !hasUnreadBadge && !!onMarkUnread && !dead;

  // The row button and the "Mark unread" action are SIBLINGS inside a `group` wrapper — a
  // <button> nested inside another <button> is invalid HTML and breaks click handling.
  return (
    <div className="relative group">
    <button
      onClick={onSelect}
      disabled={dead}
      aria-current={active ? 'true' : undefined}
      title={dead ? 'Account is dead or needs re-auth — actions disabled' : undefined}
      className={[
        'w-full text-left px-3 py-2.5 flex gap-3 border-l-2 transition-colors',
        active
          ? 'bg-accent/10 border-accent'
          : 'border-transparent hover:bg-bg-hover',
        dead ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
        !dead && conv.isMuted ? 'opacity-60' : '',
      ].join(' ')}
    >
      <FanAvatar
        nickname={conv.fanNickname}
        username={conv.fanUsername}
        fanId={conv.fanId}
        active={active}
        size="sm"
      />

      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        {/* Creator/account label — merged "All" tab only (chatter Phase 13 D-13-01 + admin
            D-04): rendered as a legible eyebrow ABOVE the fan name so a reader instantly sees
            which creator a row belongs to (D-13-08 — bumped from the prior lowest-contrast
            text-text-subtle/text-[10px] treatment to a higher-contrast token + bolder weight
            + a slightly larger size, legible in both light and dark). This lands once in the
            shared @crm/chat component and improves BOTH the chatter All tab and the admin All
            tab. Per-creator-tab rows (showCreatorLabel=false) are unaffected. */}
        {showCreatorLabel && conv.creatorName && (
          <span className="text-[11px] font-semibold text-accent font-mono truncate">
            {conv.creatorName}
          </span>
        )}

        {/* Name + time */}
        <div className="flex items-center justify-between gap-2">
          <span
            className={[
              'text-sm text-text truncate flex items-center gap-1.5',
              needsReply ? 'font-bold' : 'font-semibold',
            ].join(' ')}
          >
            {name}
            {conv.isFanBlocked && (
              <Badge variant="danger" className="text-[10px] px-1 py-0">Blocked</Badge>
            )}
          </span>
          <span className="text-xs text-text-subtle shrink-0 font-mono">
            {relativeTime(conv.lastMessageAt ?? undefined)}
          </span>
        </div>

        {/* Preview — bold + high-contrast when the fan is awaiting a reply */}
        <span
          className={[
            'text-xs truncate',
            !conv.lastMessagePreview
              ? 'text-text-subtle italic'
              : needsReply
                ? 'text-text font-medium'
                : 'text-text-muted',
          ].join(' ')}
        >
          {conv.lastMessagePreview
            ? `${conv.lastMessageDirection === 'outbound' ? 'You: ' : ''}${conv.lastMessagePreview}`
            : 'No messages yet'}
        </span>

        {/* Reply / muted signal + health + spend + unread */}
        <div className="flex items-center justify-between gap-2">
          {conv.isMuted ? (
            <Badge variant="default" className="text-[10px] px-1 py-0 uppercase tracking-wide">
              Muted
            </Badge>
          ) : needsReply ? (
            <Badge variant="warning" className="text-[10px] font-bold px-1 py-0 uppercase tracking-wide">
              Reply
            </Badge>
          ) : (
            <span aria-hidden="true" />
          )}
          <div className="flex items-center gap-1.5 shrink-0">
            {conv.totalSpendForCreator != null && conv.totalSpendForCreator > 0 && (
              <Badge
                variant="success"
                className="font-mono font-bold tabular-nums"
                title="Total spent by this fan"
              >
                {formatEurMinor(conv.totalSpendForCreator)}
              </Badge>
            )}
            {healthState && healthState !== 'online' && (
              <AccountHealthBadge state={healthState} compact />
            )}
            {/* Phase 10: show the bubble when Maloum has unread OR the CRM-local override is
                set (D-10-03/SC3). When only the override is set (unread 0) show a "!". */}
            {(conv.unread > 0 || conv.isMarkedUnread) && (
              <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1 rounded-full bg-accent text-white text-[11px] font-bold leading-none">
                {conv.unread > 0 ? (conv.unread > 99 ? '99+' : conv.unread) : '!'}
              </span>
            )}
          </div>
        </div>
      </div>
      </button>

      {/* Hover-revealed unread-badge toggle — a SIBLING of the row button (not nested), so it
          never triggers row-select (stopPropagation) and stays valid HTML. Keyboard-reachable
          (focus:opacity-100). Shows "Mark read" when a badge is present, "Mark unread" otherwise
          (mutually exclusive). Both are CRM-local (no Maloum call); hidden when the account is
          dead. */}
      {(showMarkRead || showMarkUnread) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (showMarkRead) {
              onMarkRead?.();
            } else {
              onMarkUnread?.();
            }
          }}
          title={showMarkRead ? 'Mark read' : 'Mark unread'}
          aria-label={`Mark conversation with ${name} as ${showMarkRead ? 'read' : 'unread'}`}
          className={[
            'absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded px-1.5 py-0.5',
            'text-[10px] font-semibold uppercase tracking-wide',
            'bg-bg-card border border-border text-text-subtle hover:text-accent hover:border-accent',
            'opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity',
            'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent/60',
          ].join(' ')}
        >
          {showMarkRead ? 'Mark read' : 'Mark unread'}
        </button>
      )}
    </div>
  );
}

